/**
 * RapportCriteres.jsx — Rapport pédagogique « Critères de réussite ».
 *
 * Généré automatiquement depuis les trames (Plateau, 125, Passerelle) et
 * converti en tutoiement via `utils/toTu.js`. Conçu pour :
 *   - affichage salle (projection ou impression A4/A3)
 *   - remise à l'élève en fin de formation (résumé « Tu as réussi quand… »)
 *
 * Toolbar d'entête :
 *   - Imprimer / PDF (window.print)
 *   - Copier tout le rapport en Markdown
 *
 * Route : /rapport-criteres
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Copy, Check, CheckCircle2, Clock } from 'lucide-react';
import { toTu } from '../utils/toTu';
import { PHASES as PLATEAU_PHASES } from '../data/plateauProgression';
import { PHASES_125 } from '../data/formation125Progression';
import { PHASES_PASSERELLE } from '../data/passerelleA2AProgression';

const COURS = [
  {
    id: 'form125',
    title: 'Formation 125 cm³ — 7 h',
    subtitle: 'Passerelle B → 125 cm³',
    color: '#e11d48',
    phases: PHASES_125,
    trameTo: '/formation-125-progression',
  },
  {
    id: 'plateau',
    title: 'Plateau A1 / A2',
    subtitle: 'Phases P1 · P2 · P3',
    color: '#0891b2',
    phases: PLATEAU_PHASES,
    trameTo: '/plateau-progression',
  },
  {
    id: 'passerelle',
    title: 'Passerelle A2 → A',
    subtitle: '7 h · Compléments motard confirmé',
    color: '#ea580c',
    phases: PHASES_PASSERELLE,
    trameTo: '/passerelle-a2-a-progression',
  },
];

function CriteresTable({ phase, color }) {
  const rows = (phase.exercises || []).filter(ex => ex.critere);
  if (!rows.length) return null;
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#141416] print:border-black/25 print:break-inside-avoid mb-6">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-white/10 print:border-black/25">
            <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white/60 print:text-black/70 w-1/3" style={{ color }}>
              Compétence
            </th>
            <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white/60 print:text-black/70" style={{ color }}>
              Tu as réussi quand…
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((ex, i) => (
            <tr
              key={ex.id}
              className={`border-b border-white/5 print:border-black/15 ${i % 2 === 0 ? 'bg-white/[0.015]' : ''}`}
              data-testid={`rapport-row-${ex.id}`}
            >
              <td className="px-4 py-3 align-top">
                <div className="text-white font-black text-[13.5px] leading-tight print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {ex.title}
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className="inline-flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color }} />
                  <p className="text-white/85 text-[13.5px] leading-relaxed print:text-black/85">{toTu(ex.critere)}</p>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildMarkdown() {
  const lines = [];
  lines.push('# Rapport pédagogique — Critères de réussite (mode élève)');
  lines.push('');
  lines.push('_Généré depuis les trames CODE 2 ROUES · Format prêt pour affichage salle._');
  lines.push('');
  COURS.forEach(cours => {
    lines.push(`## ${cours.title}`);
    lines.push('');
    cours.phases.forEach(phase => {
      lines.push(`### ${phase.code || phase.id} · ${phase.title}${phase.duration ? ' (' + phase.duration + ')' : ''}`);
      lines.push('');
      lines.push('| Compétence | Tu as réussi quand… |');
      lines.push('|---|---|');
      (phase.exercises || []).forEach(ex => {
        if (!ex.critere) return;
        const critere = toTu(ex.critere).replace(/\|/g, '\\|').replace(/\n/g, ' ');
        const titre = ex.title.replace(/\|/g, '\\|');
        lines.push(`| **${titre}** | ${critere} |`);
      });
      lines.push('');
    });
  });
  return lines.join('\n');
}

export default function RapportCriteres() {
  const [copied, setCopied] = useState(false);
  const md = useMemo(() => buildMarkdown(), []);

  const totalCriteres = useMemo(() => {
    let n = 0;
    COURS.forEach(c => c.phases.forEach(p => p.exercises.forEach(ex => { if (ex.critere) n++; })));
    return n;
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      toast.success('Rapport copié — colle-le dans un document ou un mail.');
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast.error('Copie impossible. Utilise Imprimer/PDF à la place.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 print:bg-white print:text-black" data-testid="rapport-criteres">
      {/* Header non-print */}
      <div className="print:hidden border-b border-white/10 bg-black/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/formations-2-roues" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/85 text-[12px] font-black uppercase tracking-widest transition" data-testid="rapport-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour hub trames
          </Link>
          <div className="flex-1" />
          <button type="button" onClick={handleCopy} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-emerald-500/40 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/40 text-[12px] font-black uppercase tracking-widest transition" data-testid="rapport-copy">
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copié !' : 'Copier (Markdown)'}
          </button>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/15 bg-white hover:bg-white/95 text-black text-[12px] font-black uppercase tracking-widest transition" data-testid="rapport-print">
            <Printer className="w-3.5 h-3.5" /> Imprimer / PDF
          </button>
        </div>
      </div>

      {/* Hero */}
      <header className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[10px] font-black uppercase tracking-widest mb-3 print:bg-emerald-50 print:text-emerald-800 print:border-emerald-300">
          <CheckCircle2 className="w-3 h-3" /> Rapport pédagogique · Version élève
        </div>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Critères de réussite — « Tu as réussi quand… »
        </h1>
        <p className="text-white/70 text-sm sm:text-base mt-2 max-w-3xl leading-relaxed print:text-black/70">
          {totalCriteres} critères d'auto-évaluation en 2ᵉ personne, générés automatiquement depuis les trames pédagogiques CODE 2 ROUES. Prêt pour affichage salle ou remise à l'élève.
        </p>
        <div className="mt-3 text-[11px] text-white/50 print:text-black/50">
          Formation 125 · Plateau A1/A2 · Passerelle A2 → A
        </div>
      </header>

      {/* Contenu principal */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 space-y-10 print:space-y-6">
        {COURS.map(cours => (
          <section key={cours.id} className="print:break-before-page first:print:break-before-auto" data-testid={`rapport-cours-${cours.id}`}>
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-black leading-tight print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: cours.color }}>
                  {cours.title}
                </h2>
                <p className="text-white/60 text-[13px] mt-1 print:text-black/60">{cours.subtitle}</p>
              </div>
              <Link to={cours.trameTo} className="print:hidden text-[11px] font-black uppercase tracking-widest text-white/60 hover:text-white transition">
                Ouvrir la trame →
              </Link>
            </div>

            {cours.phases.map(phase => (
              <div key={phase.id} className="mb-5">
                <div className="flex items-baseline gap-3 flex-wrap mb-2">
                  <span className="font-mono font-black tracking-widest text-[11px]" style={{ color: cours.color }}>{phase.code || phase.id}</span>
                  <h3 className="text-white text-lg sm:text-xl font-black print:text-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {phase.title}
                  </h3>
                  {phase.duration && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-white/60 print:text-black/60">
                      <Clock className="w-3 h-3" /> {phase.duration}
                    </span>
                  )}
                </div>
                <CriteresTable phase={phase} color={cours.color} />
              </div>
            ))}
          </section>
        ))}
      </main>

      {/* Footer print */}
      <footer className="hidden print:block max-w-6xl mx-auto px-6 py-4 text-[10px] text-black/60 border-t border-black/15 mt-4">
        Rapport pédagogique CODE 2 ROUES — Critères de réussite en tutoiement · Généré depuis les trames officielles.
      </footer>
    </div>
  );
}
